import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FileText, Video, Image as ImageIcon, File, FileSpreadsheet,
  ChevronRight, Search, Upload, Trash2, Eye, Plus, X,
  ScrollText, Loader2, Download, Pencil, BookOpen, GripVertical,
  Bold, Italic, List, Link as LinkIcon, Play, Film,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import SubTabs from "@/components/SubTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RETNING_LABEL = { kosmetolog: "Kosmetolog", frisor: "Frisør" };

// Foruddefinerede farver til moduler
const MODUL_FARVER = [
  "#FF2A6D", "#F59E0B", "#10B981", "#3B82F6",
  "#8B5CF6", "#EC4899", "#14B8A6", "#EF4444",
  "#6366F1", "#84CC16",
];

// Emoji-valg til moduler og emner — grupperet efter tema
const EMOJI_VALG_UNDERVISNING = [
  // Bog/læring
  "📖", "📚", "✏️", "🧠", "💡",
  // Hud/skønhed
  "✨", "💆", "💄", "💅", "🪞",
  // Krop/sundhed
  "🌿", "💧", "🩺", "🌱", "🫧",
  // Værktøjer
  "🧴", "🧼", "✂️", "💇", "🖌️",
  // Specifik
  "👁️", "👄", "💎", "🌸", "🌺",
  "💋", "🦷", "👃", "🦴", "🩹",
  // Kategorier
  "🎯", "🏆", "⭐", "🎓", "📋",
  "🔬", "🧪", "📊", "🎬", "📷",
];

// Backend felt-navn helpers: backend bruger `titel`, men gammel kode/cache
// kan stadig levere `navn`. Disse helpers giver os ét sted at læse fra.
const modulTitel = (m) => (m?.titel ?? m?.navn ?? "");
const emneTitel = (e) => (e?.titel ?? e?.navn ?? "");

// ── Hjælpere ─────────────────────────────────────────────────────────────────

// Max filstørrelse for upload — skal matche backend (UNDERVISNING_MAX_FIL_BYTES).
const UNDERVISNING_MAX_FIL_MB = 50;
const UNDERVISNING_MAX_FIL_BYTES_FE = UNDERVISNING_MAX_FIL_MB * 1024 * 1024;

// Tilladte fil-endelser (skal matche backend-whitelist).
const UNDERVISNING_TILLADTE_ENDELSER_FE = new Set([
  "jpg", "jpeg", "png", "webp", "gif",
  "pdf",
  "ppt", "pptx",
  "doc", "docx",
  "xls", "xlsx",
  "csv",
  "mp4", "mov",
]);

function _filEndelse(navn) {
  if (!navn || navn.indexOf(".") < 0) return "";
  return navn.split(".").pop().toLowerCase();
}

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("da-DK", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

// Returnér Icon-komponent + tailwind-farve baseret på filtype.
// Video-links får eget rosa Video-ikon, uafhængigt af filnavn/ext.
function fileIcon(filnavn = "", mimeType = "", erVideoLink = false) {
  if (erVideoLink) return { Icon: Video, color: "text-pink-500" };
  const ext = (filnavn.split(".").pop() || "").toLowerCase();
  const mt = (mimeType || "").toLowerCase();
  if (ext === "pdf" || mt === "application/pdf")
    return { Icon: FileText, color: "text-red-500" };
  if (["ppt", "pptx"].includes(ext) || mt.includes("presentation"))
    return { Icon: FileText, color: "text-orange-500" };
  if (["doc", "docx"].includes(ext) || mt.includes("word"))
    return { Icon: FileText, color: "text-blue-500" };
  if (["xls", "xlsx", "csv"].includes(ext) || mt.includes("excel") || mt === "text/csv")
    return { Icon: FileSpreadsheet, color: "text-green-500" };
  if (["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext) || mt.startsWith("image/"))
    return { Icon: ImageIcon, color: "text-purple-500" };
  if (["mp4", "mov", "webm", "avi", "mkv"].includes(ext) || mt.startsWith("video/"))
    return { Icon: Video, color: "text-pink-500" };
  return { Icon: File, color: "text-muted-foreground" };
}

// Åbn et eksternt video-link (Vimeo/YouTube/andet) i en ny browserfane.
// Vi bygger ALDRIG vores egen player oven på eksterne kilder — link åbnes direkte.
// Normaliserer URL'en (tilføj https:// hvis præfiks mangler).
function openExternalVideo(url) {
  const raw = (url || "").trim();
  if (!raw) return;
  const normaliseret = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  window.open(normaliseret, "_blank", "noopener,noreferrer");
}

// ── Bekræft-modal (generisk) ─────────────────────────────────────────────────

function ConfirmModal({ titel, besked, bekraeftTekst = "Slet", onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [fejl, setFejl] = useState(null);
  const handle = async () => {
    setBusy(true);
    setFejl(null);
    try { await onConfirm(); onClose(); }
    catch (e) { setFejl(e?.response?.data?.detail || e?.message || "Kunne ikke gennemføre handlingen"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/10 backdrop-blur-[2px] p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-xl shadow-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-sm mb-2">{titel}</h3>
        <p className="text-sm text-muted-foreground mb-4">{besked}</p>
        {fejl && (
          <p className="text-[13px] text-red-600 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-4">{fejl}</p>
        )}
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
          >
            Annullér
          </button>
          <button
            onClick={handle}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : bekraeftTekst}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Rich-text editor (minimal) ───────────────────────────────────────────────

function RichTextEditor({ value, onChange, placeholder = "Skriv beskrivelse..." }) {
  const ref = useRef(null);

  // Sæt HTML én gang ved mount; håndter ændringer via onInput for at undgå caret-jump
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || "");
  };

  const handleLink = () => {
    const url = window.prompt("URL:", "https://");
    if (url) exec("createLink", url);
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-secondary/30">
        <button type="button" onClick={() => exec("bold")} title="Fed" className="p-1.5 rounded hover:bg-secondary transition-colors">
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => exec("italic")} title="Kursiv" className="p-1.5 rounded hover:bg-secondary transition-colors">
          <Italic className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => exec("insertUnorderedList")} title="Liste" className="p-1.5 rounded hover:bg-secondary transition-colors">
          <List className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={handleLink} title="Link" className="p-1.5 rounded hover:bg-secondary transition-colors">
          <LinkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="px-3 py-2.5 text-sm min-h-[100px] max-h-[260px] overflow-y-auto focus:outline-none"
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
}

// ── Modul-modal (opret/rediger) ──────────────────────────────────────────────

// ── Emoji-vælger ─────────────────────────────────────────────────────────────

function EmojiPicker({ value, onChange }) {
  return (
    <div>
      <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Emoji</label>
      <div className="flex flex-wrap gap-1 mb-2 p-2 rounded-xl border border-border bg-secondary/30 max-h-40 overflow-y-auto">
        {EMOJI_VALG_UNDERVISNING.map(e => (
          <button
            key={e}
            type="button"
            onClick={() => onChange(e)}
            className={`text-2xl p-1.5 rounded-lg hover:bg-secondary transition-colors ${value === e ? "bg-[#FF2A6D]/10 ring-2 ring-[#FF2A6D]" : ""}`}
            title={e}
          >
            {e}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={e => onChange(e.target.value.slice(0, 4))}
          placeholder="Eller indsæt egen emoji..."
          maxLength={4}
          className="flex-1 px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
        />
        <button
          type="button"
          onClick={() => onChange("")}
          className="px-3 py-2 rounded-xl border border-border text-sm hover:bg-secondary"
        >
          Ryd
        </button>
      </div>
    </div>
  );
}

// ── Modul-modal (opret/rediger) ──────────────────────────────────────────────

function ModulModal({ titel, initial = {}, foreslaaetRaekkefolge, onClose, onSave, onDirtyChange, onSaved }) {
  // Backend bruger `titel` + `rakefolge`. Acceptér både nye og gamle feltnavne
  // som initial-input så cache uden migrering ikke taber data.
  const initialTitel = initial.titel ?? initial.navn ?? "";
  const initialBeskrivelse = initial.beskrivelse ?? "";
  const initialRakefolge = initial.rakefolge ?? initial.raekkefolge ?? foreslaaetRaekkefolge ?? 0;
  const initialFarve = initial.farve || MODUL_FARVER[0];
  const initialIkon = initial.ikon ?? "";

  const [titelInput, setTitelInput] = useState(initialTitel);
  const [beskrivelse, setBeskrivelse] = useState(initialBeskrivelse);
  const [rakefolge, setRakefolge] = useState(initialRakefolge);
  const [farve, setFarve] = useState(initialFarve);
  const [ikon, setIkon] = useState(initialIkon);
  const [busy, setBusy] = useState(false);

  const dirty =
    titelInput.trim() !== String(initialTitel).trim() ||
    beskrivelse.trim() !== String(initialBeskrivelse).trim() ||
    Number(rakefolge) !== Number(initialRakefolge) ||
    farve !== initialFarve ||
    (ikon || "") !== (initialIkon || "");

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const doSave = async () => {
    if (!titelInput.trim()) { alert("Titel er påkrævet"); return false; }
    setBusy(true);
    try {
      await onSave({
        titel: titelInput.trim(),
        beskrivelse: beskrivelse.trim(),
        rakefolge: Number(rakefolge) || 0,
        farve,
        ikon: (ikon || "").trim(),
      });
      onSaved?.("Modul gemt");
      setBusy(false);
      return true;
    } catch (e) {
      setBusy(false);
      alert(e?.response?.data?.detail || "Kunne ikke gemme modul");
      return false;
    }
  };

  const handleSave = async () => {
    const ok = await doSave();
    if (ok) onClose();
  };

  const handleCancel = async () => {
    if (!dirty) { onClose(); return; }
    const valg = window.prompt(
      "Du har ugemte ændringer.\n\nSkriv 'gem' for at gemme, 'forkast' for at kassere, eller annullér for at blive.",
      "",
    );
    if (valg == null) return;
    const v = valg.trim().toLowerCase();
    if (v === "gem") {
      const ok = await doSave();
      if (ok) onClose();
    } else if (v === "forkast") {
      onClose();
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, titelInput, beskrivelse, rakefolge, farve, ikon]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-4 gap-3">
          <h3 className="font-semibold text-sm">{titel}</h3>
          <button onClick={handleCancel} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Titel</label>
            <input
              type="text"
              value={titelInput}
              onChange={e => setTitelInput(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Beskrivelse</label>
            <textarea
              value={beskrivelse}
              onChange={e => setBeskrivelse(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1.5 block">Rækkefølge</label>
              <input
                type="number"
                value={rakefolge}
                onChange={e => setRakefolge(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1.5 block">Farve</label>
              <div className="flex flex-wrap gap-1.5">
                {MODUL_FARVER.map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFarve(f)}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${farve === f ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: f }}
                    title={f}
                  />
                ))}
              </div>
            </div>
          </div>

          <EmojiPicker value={ikon} onChange={setIkon} />
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
          >
            Annullér
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Emne-modal (opret/rediger) ───────────────────────────────────────────────

function EmneModal({ titel, initial = {}, foreslaaetRaekkefolge, onClose, onSave, onDirtyChange, onSaved }) {
  // Backend bruger `titel` + `rakefolge`. Acceptér både feltnavne for input.
  const initialTitel = initial.titel ?? initial.navn ?? "";
  const initialBeskrivelse = initial.beskrivelse ?? "";
  const initialRakefolge = initial.rakefolge ?? initial.raekkefolge ?? foreslaaetRaekkefolge ?? 0;
  const initialIkon = initial.ikon ?? "";

  const [titelInput, setTitelInput] = useState(initialTitel);
  const [beskrivelse, setBeskrivelse] = useState(initialBeskrivelse);
  const [rakefolge, setRakefolge] = useState(initialRakefolge);
  const [ikon, setIkon] = useState(initialIkon);
  const [busy, setBusy] = useState(false);

  const dirty =
    titelInput.trim() !== String(initialTitel).trim() ||
    (beskrivelse || "") !== (initialBeskrivelse || "") ||
    Number(rakefolge) !== Number(initialRakefolge) ||
    (ikon || "") !== (initialIkon || "");

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const doSave = async () => {
    if (!titelInput.trim()) { alert("Titel er påkrævet"); return false; }
    setBusy(true);
    try {
      await onSave({
        titel: titelInput.trim(),
        beskrivelse: beskrivelse,
        rakefolge: Number(rakefolge) || 0,
        ikon: (ikon || "").trim(),
      });
      onSaved?.("Emne gemt");
      setBusy(false);
      return true;
    } catch (e) {
      setBusy(false);
      alert(e?.response?.data?.detail || "Kunne ikke gemme emne");
      return false;
    }
  };

  const handleSave = async () => {
    const ok = await doSave();
    if (ok) onClose();
  };

  const handleCancel = async () => {
    if (!dirty) { onClose(); return; }
    const valg = window.prompt(
      "Du har ugemte ændringer.\n\nSkriv 'gem' for at gemme, 'forkast' for at kassere, eller annullér for at blive.",
      "",
    );
    if (valg == null) return;
    const v = valg.trim().toLowerCase();
    if (v === "gem") {
      const ok = await doSave();
      if (ok) onClose();
    } else if (v === "forkast") {
      onClose();
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, titelInput, beskrivelse, rakefolge, ikon]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-lg">
        <div className="flex items-start justify-between mb-4 gap-3">
          <h3 className="font-semibold text-sm">{titel}</h3>
          <button onClick={handleCancel} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Titel</label>
            <input
              type="text"
              value={titelInput}
              onChange={e => setTitelInput(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Beskrivelse</label>
            <RichTextEditor value={beskrivelse} onChange={setBeskrivelse} />
          </div>

          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Rækkefølge</label>
            <input
              type="number"
              value={rakefolge}
              onChange={e => setRakefolge(e.target.value)}
              className="w-32 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <EmojiPicker value={ikon} onChange={setIkon} />
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
          >
            Annullér
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Synligheds-modal ─────────────────────────────────────────────────────────

function SynlighedModal({ fil, onClose, onSave }) {
  // Backend returnerer kanoniske navne (synlig_for_*); ældre cache kan have
  // kortform — vi læser begge for at vise korrekt initial-state.
  const [synligMedarbejder, setSynligMedarbejder] = useState(
    !!(fil.synlig_for_medarbejdere ?? fil.synlig_medarbejder),
  );
  const [synligElev, setSynligElev] = useState(
    !!(fil.synlig_for_elever ?? fil.synlig_elev),
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send PRÆCIS de feltnavne backend forventer.
      await onSave({
        synlig_for_medarbejdere: synligMedarbejder,
        synlig_for_elever: synligElev,
      });
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Ukendt fejl");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-1">Synlighed for</p>
            <h3 className="font-semibold text-sm break-all">{fil.filnavn}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2.5">
          <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40 cursor-not-allowed opacity-80">
            <input type="checkbox" checked readOnly className="w-4 h-4 accent-[#FF2A6D]" />
            <div className="flex-1">
              <p className="text-sm font-medium">Admins og superadmins</p>
              <p className="text-[13px] text-muted-foreground">Altid synlig</p>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40 hover:bg-secondary/60 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={synligMedarbejder}
              onChange={e => setSynligMedarbejder(e.target.checked)}
              className="w-4 h-4 accent-[#FF2A6D]"
            />
            <span className="text-sm font-medium">Medarbejdere</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40 hover:bg-secondary/60 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={synligElev}
              onChange={e => setSynligElev(e.target.checked)}
              className="w-4 h-4 accent-[#FF2A6D]"
            />
            <span className="text-sm font-medium">Elever</span>
          </label>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
          >
            Annullér
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload-modal (udvidet med drag-and-drop + beskrivelse + video-link tab) ──

function UploadModal({ emneNavn, onClose, onUpload, onVideoLink }) {
  const [tab, setTab] = useState("fil"); // 'fil' | 'video'

  // Fil-state
  const [file, setFile] = useState(null);
  const [filnavn, setFilnavn] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [fejl, setFejl] = useState(null);
  const fileRef = useRef(null);

  // Video-link-state
  const [videoTitel, setVideoTitel] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);

  // Delt state (synlighed + beskrivelse — samme felter på begge tabs)
  const [beskrivelse, setBeskrivelse] = useState("");
  const [synligMedarbejder, setSynligMedarbejder] = useState(false);
  const [synligElev, setSynligElev] = useState(false);

  const handleFile = (f) => {
    setFejl(null);
    if (!f) { setFile(null); return; }
    // Klient-side validering INDEN upload.
    const endelse = _filEndelse(f.name);
    if (!UNDERVISNING_TILLADTE_ENDELSER_FE.has(endelse)) {
      setFejl(`Filtypen ".${endelse || "?"}" understøttes ikke. Tilladt: billeder, PDF, PowerPoint, Word, Excel, CSV, mp4/mov.`);
      setFile(null);
      return;
    }
    if (f.size > UNDERVISNING_MAX_FIL_BYTES_FE) {
      setFejl(`Filen er for stor (${fmtBytes(f.size)}). Max ${UNDERVISNING_MAX_FIL_MB} MB.`);
      setFile(null);
      return;
    }
    setFile(f);
    if (!filnavn) setFilnavn(f.name);
  };

  const handleUpload = async () => {
    if (!file) return;
    // Genvalidér før send.
    if (file.size > UNDERVISNING_MAX_FIL_BYTES_FE) {
      setFejl(`Filen er for stor. Max ${UNDERVISNING_MAX_FIL_MB} MB.`);
      return;
    }
    setFejl(null);
    setUploading(true);
    setProgress(0);
    const tick = setInterval(() => setProgress(p => Math.min(p + 7, 92)), 150);
    try {
      await onUpload(file, {
        filnavn: filnavn.trim() || file.name,
        beskrivelse: beskrivelse.trim(),
        synlig_for_medarbejdere: synligMedarbejder,
        synlig_for_elever: synligElev,
      });
      setProgress(100);
      onClose();
    } catch (e) {
      setFejl(e?.response?.data?.detail || e?.message || "Upload fejlede");
    } finally {
      clearInterval(tick);
      setUploading(false);
    }
  };

  const handleVideoLink = async () => {
    const t = videoTitel.trim();
    const u = videoUrl.trim();
    if (!t || !u) { alert("Titel og URL er påkrævet"); return; }
    if (!/^https?:\/\//i.test(u)) { alert("URL skal starte med http:// eller https://"); return; }
    if (!onVideoLink) { alert("Video-link er ikke understøttet"); return; }
    setVideoBusy(true);
    try {
      await onVideoLink({
        titel: t,
        url: u,
        beskrivelse: beskrivelse.trim(),
        synlig_medarbejder: synligMedarbejder,
        synlig_elev: synligElev,
      });
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke tilføje video-link");
    } finally {
      setVideoBusy(false);
    }
  };

  const busy = uploading || videoBusy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-1">Tilføj materiale</p>
            <h3 className="font-semibold text-sm">{emneNavn}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab-bar */}
        <div className="flex gap-1 mb-4 p-1 bg-secondary rounded-full w-fit">
          <button
            type="button"
            onClick={() => setTab("fil")}
            disabled={busy}
            className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors inline-flex items-center gap-1.5
              ${tab === "fil" ? "bg-[#FF2A6D] text-white shadow" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Upload className="w-3 h-3" /> Upload fil
          </button>
          <button
            type="button"
            onClick={() => setTab("video")}
            disabled={busy}
            className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors inline-flex items-center gap-1.5
              ${tab === "video" ? "bg-[#FF2A6D] text-white shadow" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Film className="w-3 h-3" /> Video link
          </button>
        </div>

        <div className="space-y-4">
          {tab === "fil" ? (
            <>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  onChange={e => handleFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleFile(f);
                  }}
                  onClick={() => fileRef.current?.click()}
                  className={`w-full flex flex-col items-center justify-center gap-2 px-3 py-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors
                    ${dragOver
                      ? "border-[#FF2A6D] bg-[#FF2A6D]/5"
                      : file
                        ? "border-border bg-secondary/30"
                        : "border-border hover:bg-secondary/40"}`}
                >
                  <Upload className={`w-5 h-5 ${dragOver ? "text-[#FF2A6D]" : "text-muted-foreground"}`} />
                  <span className={file ? "text-foreground font-medium text-sm truncate max-w-full" : "text-muted-foreground text-sm"}>
                    {file ? file.name : "Træk fil hertil eller klik for at vælge"}
                  </span>
                  {file && (
                    <p className="text-[13px] text-muted-foreground">{fmtBytes(file.size)}</p>
                  )}
                </div>
                <p className="text-[12px] text-muted-foreground mt-1.5">
                  Max filstørrelse: {UNDERVISNING_MAX_FIL_MB} MB. Tilladte typer: billeder, PDF, PowerPoint, Word, Excel, CSV, mp4/mov.
                </p>
                {fejl && (
                  <p className="text-[13px] text-red-500 mt-1.5 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30">
                    {fejl}
                  </p>
                )}
              </div>

              {file && (
                <div>
                  <label className="text-[13px] text-muted-foreground mb-1.5 block">Filnavn</label>
                  <input
                    type="text"
                    value={filnavn}
                    onChange={e => setFilnavn(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1.5 block">Titel</label>
                <input
                  type="text"
                  value={videoTitel}
                  onChange={e => setVideoTitel(e.target.value)}
                  autoFocus
                  placeholder="F.eks. Anatomi-introduktion"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1.5 block">URL (YouTube, Vimeo eller andet)</label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={e => setVideoUrl(e.target.value)}
                  placeholder="https://"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                />
              </div>
            </>
          )}

          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Beskrivelse (valgfri)</label>
            <textarea
              value={beskrivelse}
              onChange={e => setBeskrivelse(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <div className="space-y-2">
            <p className="text-[13px] text-muted-foreground font-medium uppercase tracking-wider">Synlighed</p>
            <label className="flex items-center gap-2.5 text-sm cursor-not-allowed opacity-70">
              <input
                type="checkbox"
                checked
                disabled
                className="w-4 h-4 accent-[#FF2A6D]"
              />
              Admins og superadmins <span className="text-[13px] text-muted-foreground">(altid)</span>
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={synligMedarbejder}
                onChange={e => setSynligMedarbejder(e.target.checked)}
                className="w-4 h-4 accent-[#FF2A6D]"
              />
              Medarbejdere
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={synligElev}
                onChange={e => setSynligElev(e.target.checked)}
                className="w-4 h-4 accent-[#FF2A6D]"
              />
              Elever
            </label>
            {tab === "fil" && (
              <p className="text-[12px] text-muted-foreground leading-relaxed mt-2 px-3 py-2 rounded-md bg-secondary/40 border border-border/60">
                <span className="font-semibold text-foreground">Tip:</span> Upload "prøve" og "prøve svar" samtidigt — markér svar som
                kun synligt for medarbejdere, så elever ikke kan hente facit.
              </p>
            )}
          </div>

          {uploading && (
            <div>
              <div className="flex items-center justify-between text-[13px] text-muted-foreground mb-1">
                <span>Uploader...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full bg-[#FF2A6D] transition-all duration-150"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            Annullér
          </button>
          {tab === "fil" ? (
            <button
              onClick={handleUpload}
              disabled={busy || !file}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2"
            >
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploader...</> : "Upload"}
            </button>
          ) : (
            <button
              onClick={handleVideoLink}
              disabled={busy || !videoTitel.trim() || !videoUrl.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2"
            >
              {videoBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Gemmer...</> : "Tilføj video link"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Rediger-fil-modal ────────────────────────────────────────────────────────

function RedigerFilModal({ fil, onClose, onSave }) {
  const [filnavn, setFilnavn] = useState(fil.filnavn || "");
  const [beskrivelse, setBeskrivelse] = useState(fil.beskrivelse || "");
  // Læs både kanonisk navn og kortform for backward-compat.
  const [synligMedarbejder, setSynligMedarbejder] = useState(
    !!(fil.synlig_for_medarbejdere ?? fil.synlig_medarbejder),
  );
  const [synligElev, setSynligElev] = useState(
    !!(fil.synlig_for_elever ?? fil.synlig_elev),
  );
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!filnavn.trim()) { alert("Filnavn er påkrævet"); return; }
    setBusy(true);
    try {
      await onSave({
        filnavn: filnavn.trim(),
        beskrivelse: beskrivelse.trim(),
        synlig_for_medarbejdere: synligMedarbejder,
        synlig_for_elever: synligElev,
      });
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Kunne ikke gemme");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-4 gap-3">
          <h3 className="font-semibold text-sm">Rediger fil</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Filnavn</label>
            <input
              type="text"
              value={filnavn}
              onChange={e => setFilnavn(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <div>
            <label className="text-[13px] text-muted-foreground mb-1.5 block">Beskrivelse</label>
            <textarea
              value={beskrivelse}
              onChange={e => setBeskrivelse(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={synligMedarbejder}
                onChange={e => setSynligMedarbejder(e.target.checked)}
                className="w-4 h-4 accent-[#FF2A6D]"
              />
              Synlig for medarbejdere
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={synligElev}
                onChange={e => setSynligElev(e.target.checked)}
                className="w-4 h-4 accent-[#FF2A6D]"
              />
              Synlig for elever
            </label>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
          >
            Annullér
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Aktivitetslog-modal ──────────────────────────────────────────────────────

function AktivitetslogModal({ token, onClose }) {
  const [log, setLog] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/undervisning/log`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLog(r.data?.log || r.data?.entries || r.data || []))
      .catch(e => setError(e.response?.data?.detail || e.message || "Ukendt fejl"));
  }, [token]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-[#FF2A6D]" />
            <h3 className="font-semibold text-sm">Aktivitetslog</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error ? (
            <p className="text-sm text-red-500 text-center py-8">{error}</p>
          ) : !log ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : log.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Ingen aktivitet endnu</p>
          ) : (
            <div className="space-y-2">
              {log.map((entry, i) => (
                <div key={entry.id || i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30 text-[13px]">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">
                      <span className="text-foreground">{entry.bruger || entry.user || "Ukendt"}</span>
                      <span className="text-muted-foreground"> · {entry.handling || entry.action || "—"}</span>
                    </p>
                    {(entry.detaljer || entry.details) && (
                      <p className="text-muted-foreground mt-0.5 break-words">{entry.detaljer || entry.details}</p>
                    )}
                  </div>
                  <span className="text-muted-foreground text-[13px] shrink-0">
                    {fmtTs(entry.tidspunkt || entry.timestamp || entry.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Emne (foldbar liste over filer) ──────────────────────────────────────────

function EmneCard({
  emne, modul, modulFarve, retning, token, isOpen, onToggle,
  filer, onFilerLoaded,
  canUpload, isAdmin,
  onBreadcrumb,
  onRediger, onSlet,
  onDragStart, onDragOver, onDrop, dropTarget,
  onOmsorterFiler, onFlytFil,
}) {
  const emneFarve = modulFarve || modul?.farve || "#FF2A6D";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [redigerFil, setRedigerFil] = useState(null);
  const [synlighedFor, setSynlighedFor] = useState(null);
  const [sletFil, setSletFil] = useState(null);
  const [filDropTarget, setFilDropTarget] = useState(null);
  const [forhaandsvis, setForhaandsvis] = useState(null);

  // Lazy-load filer ved første åbning
  useEffect(() => {
    if (!isOpen || filer != null) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/undervisning/${retning}/emner/${emne.emne_id}/filer`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => onFilerLoaded(emne.emne_id, r.data?.filer || r.data || []))
      .catch(e => setError(e.response?.data?.detail || e.message || "Kunne ikke hente filer"))
      .finally(() => setLoading(false));
  }, [isOpen, filer, retning, emne.emne_id, token, onFilerLoaded]);

  // Brødsmule når emnet åbnes
  useEffect(() => {
    if (isOpen) onBreadcrumb?.(emneTitel(emne));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDownload = (fil) => {
    const url = `${API}/undervisning/filer/${fil.fil_id}?token=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  const handleDelete = async (fil) => {
    try {
      await axios.delete(`${API}/undervisning/filer/${fil.fil_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onFilerLoaded(emne.emne_id, (filer || []).filter(f => f.fil_id !== fil.fil_id));
    } catch (e) {
      throw new Error(e.response?.data?.detail || "Kunne ikke slette filen");
    }
  };

  const handleSaveSynlighed = async (update) => {
    await axios.put(`${API}/undervisning/filer/${synlighedFor.fil_id}/synlighed`, update, {
      headers: { Authorization: `Bearer ${token}` },
    });
    onFilerLoaded(
      emne.emne_id,
      (filer || []).map(f => f.fil_id === synlighedFor.fil_id ? { ...f, ...update } : f),
    );
  };

  const handleSaveFil = async (update) => {
    const r = await axios.put(`${API}/undervisning/filer/${redigerFil.fil_id}`, update, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const opd = r.data?.fil || { ...redigerFil, ...update };
    onFilerLoaded(
      emne.emne_id,
      (filer || []).map(f => f.fil_id === redigerFil.fil_id ? { ...f, ...opd } : f),
    );
  };

  const handleUpload = async (file, meta) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filnavn", meta.filnavn || file.name);
    fd.append("beskrivelse", meta.beskrivelse || "");
    // Send PRÆCIS de feltnavne backend forventer (kanonisk form).
    const synlMed = meta.synlig_for_medarbejdere ?? meta.synlig_medarbejder;
    const synlElev = meta.synlig_for_elever ?? meta.synlig_elev;
    fd.append("synlig_for_medarbejdere", synlMed ? "true" : "false");
    fd.append("synlig_for_elever", synlElev ? "true" : "false");
    const r = await axios.post(
      `${API}/undervisning/${retning}/emner/${emne.emne_id}/filer/upload`,
      fd,
      { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } },
    );
    const ny = r.data?.fil || r.data;
    onFilerLoaded(emne.emne_id, [...(filer || []), ny]);
  };

  const handleOpretVideoLink = async (meta) => {
    const r = await axios.post(
      `${API}/undervisning/${retning}/emner/${emne.emne_id}/video-link`,
      {
        titel: meta.titel,
        url: meta.url,
        beskrivelse: meta.beskrivelse || "",
        synlig_for_medarbejdere: !!meta.synlig_medarbejder,
        synlig_for_elever: !!meta.synlig_elev,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const ny = r.data?.fil || r.data;
    onFilerLoaded(emne.emne_id, [...(filer || []), ny]);
  };

  // Fil-omsortering via drag-and-drop
  const handleFilDragStart = (e, fil_id) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", `fil:${fil_id}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleFilDragOver = (e, fil_id) => {
    if (!(e.dataTransfer.types || []).includes("text/plain")) return;
    e.preventDefault();
    e.stopPropagation();
    setFilDropTarget(fil_id);
  };
  const handleFilDrop = (e, target_fil_id) => {
    e.preventDefault();
    e.stopPropagation();
    const raw = e.dataTransfer.getData("text/plain");
    setFilDropTarget(null);
    if (!raw.startsWith("fil:")) return;
    const draggedId = raw.slice(4);
    if (draggedId === target_fil_id) return;
    onOmsorterFiler?.(emne.emne_id, draggedId, target_fil_id);
  };

  const isDropTarget = dropTarget === emne.emne_id;

  return (
    <div
      onDragOver={(e) => onDragOver?.(e, emne.emne_id)}
      onDrop={(e) => onDrop?.(e, emne.emne_id)}
      className={`rounded-lg border border-border/60 overflow-hidden bg-secondary/10 transition-all
        ${isDropTarget ? "ring-2 ring-[#FF2A6D] ring-offset-1" : ""}`}
      style={{ borderLeftWidth: "3px", borderLeftColor: emneFarve }}
    >
      <div className="group flex items-center justify-between gap-2 hover:bg-secondary/30 transition-colors">
        {canUpload && (
          <span
            draggable
            onDragStart={(e) => onDragStart?.(e, emne.emne_id)}
            className="pl-3 py-2.5 cursor-grab active:cursor-grabbing shrink-0"
            title="Træk for at omsortere"
          >
            <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
          </span>
        )}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 min-w-0 px-4 py-2.5 text-left"
        >
          <ChevronRight
            className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
            style={{ color: emneFarve }}
          />
          {emne.ikon && <span className="text-lg shrink-0 leading-none">{emne.ikon}</span>}
          <span
            className="text-sm font-semibold truncate ml-1"
            style={{ color: emneFarve }}
          >
            {emneTitel(emne)}
          </span>
        </button>
        {canUpload && (
          <div className="flex items-center gap-0.5 pr-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); onRediger?.(emne); }}
              title="Rediger emne"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onSlet?.(emne); }}
              title="Slet emne"
              className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className={`overflow-hidden transition-all duration-200 ease-out ${isOpen ? "max-h-[3000px]" : "max-h-0"}`}>
        <div className="px-4 pb-4 pt-2 space-y-3 border-t border-border/40">
          {emne.beskrivelse && (
            <div className="text-[13px] text-muted-foreground bg-background/60 border border-border/40 rounded-md p-3">
              <p className="font-semibold text-foreground mb-1">Note/beskrivelse</p>
              <div
                className="leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_a]:text-[#FF2A6D] [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: emne.beskrivelse }}
              />
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-[13px] text-red-500">{error}</p>
          ) : (filer || []).length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <File className="w-6 h-6 mx-auto mb-2 opacity-30" />
              <p className="text-[13px] italic">Ingen materialer endnu</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filer.map(fil => {
                const { Icon: FilIcon, color: filColor } = fileIcon(
                  fil.filnavn,
                  fil.mime_type,
                  fil.er_video_link,
                );
                return (
                <div
                  key={fil.fil_id}
                  onDragOver={(e) => handleFilDragOver(e, fil.fil_id)}
                  onDragLeave={() => setFilDropTarget(null)}
                  onDrop={(e) => handleFilDrop(e, fil.fil_id)}
                  className={`group flex items-center gap-3 px-3 py-2 rounded-md hover:bg-background/60 transition-colors
                    ${filDropTarget === fil.fil_id ? "ring-2 ring-[#FF2A6D] ring-offset-1" : ""}`}
                >
                  {canUpload && (
                    <span
                      draggable
                      onDragStart={(e) => handleFilDragStart(e, fil.fil_id)}
                      className="cursor-grab active:cursor-grabbing shrink-0"
                      title="Træk for at omsortere"
                    >
                      <GripVertical className="w-3 h-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
                    </span>
                  )}
                  <span className="shrink-0"><FilIcon className={`w-4 h-4 ${filColor}`} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{fil.filnavn}</p>
                    {fil.beskrivelse && (
                      <p className="text-[12px] text-muted-foreground/80 truncate">{fil.beskrivelse}</p>
                    )}
                    {!fil.er_video_link && fil.storrelse != null && (
                      <p className="text-[13px] text-muted-foreground/60">{fmtBytes(fil.storrelse)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => fil.er_video_link ? openExternalVideo(fil.video_url) : setForhaandsvis(fil)}
                      title={fil.er_video_link ? "Åbn video i ny fane" : "Vis"}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    {fil.er_video_link ? (
                      <button
                        onClick={() => openExternalVideo(fil.video_url)}
                        title="Åbn video i ny fane"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-pink-500 hover:bg-pink-500/10 transition-colors"
                      >
                        <Play className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDownload(fil)}
                        title="Download"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => setRedigerFil(fil)}
                          title="Rediger"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setSynlighedFor(fil)}
                          title="Synlighed"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setSletFil(fil)}
                          title="Slet"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {canUpload && (
            <div className="border-t border-border pt-3 mt-3">
              <button
                onClick={() => setShowUpload(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-border hover:border-[#FF2A6D]/40 hover:bg-[#FF2A6D]/5 transition-colors text-sm font-semibold text-muted-foreground hover:text-[#FF2A6D]"
              >
                <Upload className="w-4 h-4" />
                Tilføj fil
              </button>
            </div>
          )}

          {showUpload && (
            <UploadModal
              emneNavn={emneTitel(emne)}
              onClose={() => setShowUpload(false)}
              onUpload={handleUpload}
              onVideoLink={handleOpretVideoLink}
            />
          )}
          {redigerFil && (
            <RedigerFilModal
              fil={redigerFil}
              onClose={() => setRedigerFil(null)}
              onSave={handleSaveFil}
            />
          )}
          {synlighedFor && (
            <SynlighedModal
              fil={synlighedFor}
              onClose={() => setSynlighedFor(null)}
              onSave={handleSaveSynlighed}
            />
          )}
          {sletFil && (
            <ConfirmModal
              titel="Slet fil"
              besked={`Vil du slette "${sletFil.filnavn}"?`}
              onClose={() => setSletFil(null)}
              onConfirm={() => handleDelete(sletFil)}
            />
          )}
          {forhaandsvis && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/10 backdrop-blur-[2px] p-4"
              onClick={() => setForhaandsvis(null)}
            >
              <div
                className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="font-semibold text-sm truncate">{forhaandsvis.filnavn}</h3>
                  <button
                    onClick={() => setForhaandsvis(null)}
                    className="p-1 rounded-md hover:bg-secondary"
                    title="Luk"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-hidden">
                  {/* Eksterne video-links åbnes i ny fane (se openExternalVideo) —
                      her forhåndsvises kun uploadede filer. */}
                  <iframe
                    src={`${API}/undervisning/filer/${forhaandsvis.fil_id}?token=${encodeURIComponent(token)}`}
                    className="w-full h-full"
                    title={forhaandsvis.filnavn}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modul (foldbar liste over emner) ─────────────────────────────────────────

// Split modul-navn i prefix ("Modul 1" / "Grundmodul") og resten ("Grundmodul" / "Salgsteknik")
function splitModulNavn(navn) {
  const raw = (navn || "").trim();
  if (!raw) return { prefix: "", suffix: "" };
  // Forsøg split på " - ", " – ", " — " eller " · "
  const parts = raw.split(/\s*[-–—·]\s*/);
  if (parts.length >= 2) {
    return { prefix: parts[0], suffix: parts.slice(1).join(" - ") };
  }
  // Kun "Modul N" eller "Grundmodul" uden suffix
  return { prefix: raw, suffix: "" };
}

// Default-ikoner ud fra modul-position (0-baseret index)
const MODUL_DEFAULT_IKONER = ["📖", "🌱", "🧠", "✨", "🎯", "🏆"];
function modulIkon(modul, index) {
  if (modul?.ikon) return modul.ikon;
  if (typeof index === "number" && index >= 0 && index < MODUL_DEFAULT_IKONER.length) {
    return MODUL_DEFAULT_IKONER[index];
  }
  return null;
}

function ModulCard({
  modul, index, retning, token,
  isOpen, onToggle,
  aabneEmner, onToggleEmne,
  filerByEmne, onFilerLoaded,
  canUpload, isAdmin,
  onEmneBreadcrumb,
  onRedigerModul, onSletModul,
  onRedigerEmne, onSletEmne, onNytEmne,
  onDragStart, onDragOver, onDrop, dropTarget,
  onOmsorterEmner, onOmsorterFiler, onFlytFil,
}) {
  const emneCount = modul.emner?.length ?? 0;
  const [emneDropTarget, setEmneDropTarget] = useState(null);
  const farve = modul.farve || "#FF2A6D";
  const { prefix, suffix } = splitModulNavn(modulTitel(modul));
  const ikon = modulIkon(modul, index);

  const handleEmneDragStart = (e, emne_id) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", `emne:${emne_id}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleEmneDragOver = (e, emne_id) => {
    if (!(e.dataTransfer.types || []).includes("text/plain")) return;
    e.preventDefault();
    e.stopPropagation();
    setEmneDropTarget(emne_id);
  };
  const handleEmneDrop = (e, target_emne_id) => {
    e.preventDefault();
    e.stopPropagation();
    const raw = e.dataTransfer.getData("text/plain");
    setEmneDropTarget(null);
    if (raw.startsWith("emne:")) {
      const draggedId = raw.slice(5);
      if (draggedId === target_emne_id) return;
      onOmsorterEmner?.(modul.modul_id, draggedId, target_emne_id);
    } else if (raw.startsWith("fil:")) {
      const draggedFilId = raw.slice(4);
      onFlytFil?.(draggedFilId, target_emne_id);
    }
  };

  const isDropTarget = dropTarget === modul.modul_id;

  return (
    <div
      onDragOver={(e) => onDragOver?.(e, modul.modul_id)}
      onDrop={(e) => onDrop?.(e, modul.modul_id)}
      className={`rounded-2xl border border-border bg-card shadow-sm hover:shadow-md overflow-hidden transition-all
        ${isDropTarget ? "ring-2 ring-[#FF2A6D] ring-offset-2" : ""}`}
      style={{ borderLeftColor: farve, borderLeftWidth: "6px" }}
    >
      <div className={`group flex items-center justify-between gap-2 hover:bg-secondary/30 transition-colors ${isOpen ? "sticky top-0 bg-card z-10" : ""}`}>
        {canUpload && (
          <span
            draggable
            onDragStart={(e) => onDragStart?.(e, modul.modul_id)}
            className="pl-4 py-5 cursor-grab active:cursor-grabbing shrink-0"
            title="Træk for at omsortere"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
          </span>
        )}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 sm:gap-3 min-w-0 pl-3 pr-2 sm:px-5 py-4 sm:py-5 text-left"
        >
          <ChevronRight
            className={`w-5 h-5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
            style={{ color: farve }}
          />
          {ikon && <span className="text-xl sm:text-2xl shrink-0 leading-none">{ikon}</span>}
          {/* "Modul N" + emner-badge på første linje; modul-navn på linjen under,
              så titlen har fuld bredde og ikke brækker grimt. */}
          <span className="flex flex-col min-w-0 flex-1 leading-tight gap-0.5">
            <span className="flex items-center gap-2">
              {prefix && (
                <span
                  className="text-base sm:text-xl font-extrabold tracking-tight whitespace-nowrap"
                  style={{ color: farve }}
                >
                  {prefix}
                </span>
              )}
              <span className="text-[12px] sm:text-[13px] px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-secondary text-muted-foreground font-medium shrink-0 whitespace-nowrap">
                {emneCount} {emneCount === 1 ? "emne" : "emner"}
              </span>
            </span>
            {suffix && (
              <span className="text-base sm:text-xl font-bold text-foreground break-words">
                {suffix}
              </span>
            )}
          </span>
        </button>
        {canUpload && (
          /* Redigeringsværktøj til højre, lodret stablet — fylder mindre i bredden.
             Vises KUN ved hover (mus henover) og kun for dem med upload-rettighed
             (canUpload). */
          <div className="flex flex-col items-center gap-0.5 pr-2 sm:pr-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); onRedigerModul?.(modul); }}
              title="Rediger modul"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onSletModul?.(modul); }}
              title="Slet modul"
              className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className={`overflow-hidden transition-all duration-200 ease-out ${isOpen ? "max-h-[6000px]" : "max-h-0"}`}>
        <div className="border-t border-border">
          {modul.beskrivelse && (
            <div
              className="px-5 py-4 text-sm leading-relaxed text-foreground/80"
              style={{ background: `linear-gradient(to bottom, ${farve}14, transparent)` }}
            >
              {modul.beskrivelse}
            </div>
          )}

          <div className="border-t border-border px-5 py-4 space-y-2 bg-background/30">
          {emneCount === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm mb-1">Ingen emner i dette modul endnu</p>
              <p className="text-[13px] mb-4">Tilføj det første emne for at komme i gang.</p>
              {canUpload && (
                <button
                  onClick={() => onNytEmne?.(modul)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-3.5 h-3.5" /> Nyt emne
                </button>
              )}
            </div>
          ) : (
            <>
              {modul.emner.map(emne => (
                <EmneCard
                  key={emne.emne_id}
                  emne={emne}
                  modul={modul}
                  modulFarve={farve}
                  retning={retning}
                  token={token}
                  isOpen={aabneEmner.has(emne.emne_id)}
                  onToggle={() => onToggleEmne(emne.emne_id)}
                  filer={filerByEmne[emne.emne_id]}
                  onFilerLoaded={onFilerLoaded}
                  canUpload={canUpload}
                  isAdmin={isAdmin}
                  onBreadcrumb={(navn) => onEmneBreadcrumb(modulTitel(modul), navn)}
                  onRediger={onRedigerEmne}
                  onSlet={onSletEmne}
                  onDragStart={handleEmneDragStart}
                  onDragOver={handleEmneDragOver}
                  onDrop={handleEmneDrop}
                  dropTarget={emneDropTarget}
                  onOmsorterFiler={onOmsorterFiler}
                  onFlytFil={onFlytFil}
                />
              ))}
              {canUpload && (
                <button
                  onClick={() => onNytEmne?.(modul)}
                  className="flex items-center gap-1.5 text-[13px] font-semibold text-[#FF2A6D] hover:opacity-80 transition-opacity mt-2 pl-2"
                >
                  <Plus className="w-3.5 h-3.5" /> Nyt emne
                </button>
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Søgeresultater (flat liste) ──────────────────────────────────────────────

function SoegeResultater({ results, token, retning, isAdmin }) {
  if (!results) return null;
  if (results.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">Ingen filer matcher søgningen</p>;
  }

  const handleDownload = (fil) => {
    const url = `${API}/undervisning/filer/${fil.fil_id}?token=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  return (
    <div className="space-y-1">
      {results.map(fil => {
        const { Icon: FilIcon, color: filColor } = fileIcon(
          fil.filnavn, fil.mime_type, fil.er_video_link,
        );
        return (
        <div
          key={fil.fil_id}
          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border hover:bg-secondary/20 transition-colors"
        >
          <span className="shrink-0"><FilIcon className={`w-4 h-4 ${filColor}`} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{fil.filnavn}</p>
            <p className="text-[12px] text-muted-foreground truncate">
              {fil.modul_navn || "—"} {">"} {fil.emne_navn || "—"}
            </p>
          </div>
          {fil.er_video_link ? (
            <button
              onClick={() => openExternalVideo(fil.video_url)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-pink-500 hover:bg-pink-500/10 transition-all opacity-0 group-hover:opacity-100"
              title="Åbn video i ny fane"
            >
              <Play className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => handleDownload(fil)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all opacity-0 group-hover:opacity-100"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        );
      })}
    </div>
  );
}

// ── Inner view (kan embeddes som tab i Dashboard) ────────────────────────────

export function UndervisningView({ embedded = false, forceRetning = null }) {
  const { user, token, logout, hasRettighed } = useAuth();

  const isAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);
  const isMember = user?.role === "member";
  const isElev = user?.role === "guest";
  // Elev har ALDRIG upload-/redigér-rettigheder, uanset rettighedsflag.
  const canUpload = !isElev && (isAdmin || (hasRettighed ? hasRettighed("kan_uploade_undervisningsmateriale") : false));

  // Default-retning: forceRetning vinder (fx fra Dashboard-elev-tab),
  // ellers elev fastlåses til egen via lookup, admin/medarbejder starter med kosmetolog.
  const [retning, setRetning] = useState(forceRetning || "kosmetolog");
  const [retningResolved, setRetningResolved] = useState(!!forceRetning || !isElev);

  // Hvis forceRetning ændres udefra (fx når elevdata loader senere), opdatér state
  useEffect(() => {
    if (forceRetning) {
      setRetning(forceRetning);
      setRetningResolved(true);
    }
  }, [forceRetning]);

  // For elev uden forceRetning: hent uddannelsestype via student/kpi endpoint
  useEffect(() => {
    if (!isElev || forceRetning) return;
    axios.get(`${API}/dashboard/student/kpi?period=current`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        const t = r.data?.uddannelse_type;
        setRetning(t === "kos" ? "kosmetolog" : "frisor");
      })
      .catch(() => { /* fallback til kosmetolog */ })
      .finally(() => setRetningResolved(true));
  }, [isElev, forceRetning, token]);

  const [struktur, setStruktur] = useState(null);
  const [strukturLoading, setStrukturLoading] = useState(false);
  const [strukturError, setStrukturError] = useState(null);

  const [aabneModuler, setAabneModuler] = useState(new Set());
  const [aabneEmner, setAabneEmner] = useState(new Set());
  const [filerByEmne, setFilerByEmne] = useState({});

  const [soegning, setSoegning] = useState("");
  const [soegeResultater, setSoegeResultater] = useState(null);
  const [soegerNu, setSoegerNu] = useState(false);
  const soegTimer = useRef(null);

  const [breadcrumb, setBreadcrumb] = useState({ modul: null, emne: null });
  const [visLog, setVisLog] = useState(false);

  // CRUD-modaler
  const [modulModal, setModulModal] = useState(null);   // { mode, modul? }
  const [emneModal, setEmneModal] = useState(null);     // { mode, modul, emne? }
  const [sletModul, setSletModul] = useState(null);
  const [sletEmne, setSletEmne] = useState(null);
  const [importerer, setImporterer] = useState(false);

  // DnD modul-niveau
  const [modulDropTarget, setModulDropTarget] = useState(null);

  // Toast-feedback (succes)
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Dirty-tracking på tværs af åbne modaler — bruges til beforeunload
  const [modulDirty, setModulDirty] = useState(false);
  const [emneDirty, setEmneDirty] = useState(false);
  const anyDirty = modulDirty || emneDirty;
  useEffect(() => {
    if (!anyDirty) return undefined;
    const handler = (e) => {
      // Standard-mekanismen for at trigge browser-warning
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [anyDirty]);

  // Hent struktur
  const hentStruktur = useCallback(() => {
    if (!retningResolved) return;
    setStrukturLoading(true);
    setStrukturError(null);
    axios.get(`${API}/undervisning/${retning}/struktur`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setStruktur(r.data?.moduler || r.data?.struktur || r.data || []))
      .catch(e => {
        if (e.response?.status === 401) { logout(); return; }
        setStrukturError(e.response?.data?.detail || e.message || "Kunne ikke hente struktur");
      })
      .finally(() => setStrukturLoading(false));
  }, [retning, retningResolved, token, logout]);

  // Hent struktur når retning ændres
  useEffect(() => {
    if (!retningResolved) return;
    setStruktur(null);
    setAabneModuler(new Set());
    setAabneEmner(new Set());
    setFilerByEmne({});
    setSoegning("");
    setSoegeResultater(null);
    setBreadcrumb({ modul: null, emne: null });
    hentStruktur();
  }, [retning, retningResolved, hentStruktur]);

  // Søgning (debounced)
  useEffect(() => {
    clearTimeout(soegTimer.current);
    const q = soegning.trim();
    if (!q) {
      setSoegeResultater(null);
      setSoegerNu(false);
      return;
    }
    setSoegerNu(true);
    soegTimer.current = setTimeout(() => {
      axios.get(`${API}/undervisning/${retning}/sog?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => setSoegeResultater(r.data?.filer || r.data?.results || r.data || []))
        .catch(() => setSoegeResultater([]))
        .finally(() => setSoegerNu(false));
    }, 350);
    return () => clearTimeout(soegTimer.current);
  }, [soegning, retning, token]);

  const toggleModul = (modul_id) => {
    setAabneModuler(prev => {
      const next = new Set(prev);
      if (next.has(modul_id)) next.delete(modul_id);
      else next.add(modul_id);
      return next;
    });
    const m = (struktur || []).find(x => x.modul_id === modul_id);
    if (m) setBreadcrumb(b => ({ ...b, modul: aabneModuler.has(modul_id) ? null : modulTitel(m), emne: null }));
  };

  const toggleEmne = (emne_id) => {
    setAabneEmner(prev => {
      const next = new Set(prev);
      if (next.has(emne_id)) next.delete(emne_id);
      else next.add(emne_id);
      return next;
    });
  };

  const handleFilerLoaded = useCallback((emne_id, filer) => {
    setFilerByEmne(prev => ({ ...prev, [emne_id]: filer }));
  }, []);

  const handleEmneBreadcrumb = (modulNavn, emneNavn) => {
    setBreadcrumb({ modul: modulNavn, emne: emneNavn });
  };

  // ── CRUD-handlers: moduler ─────────────────────────────────────────────────
  // Efter hver mutation reloader vi struktur fra backend så vi viser
  // canonical data (titel/rakefolge i backend's format) i stedet for at
  // stole på optimistisk lokal state der ofte havde forkerte feltnavne.
  const opretModul = async (data) => {
    const r = await axios.post(`${API}/undervisning/${retning}/moduler`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const nyt = r.data?.modul || r.data;
    setStruktur(prev => [...(prev || []), { ...nyt, emner: nyt.emner || [] }]);
    hentStruktur();
  };

  const redigerModul = async (modul_id, data) => {
    await axios.put(`${API}/undervisning/${retning}/moduler/${modul_id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Optimistisk merge så UI'et reagerer øjeblikkeligt, derefter reload.
    setStruktur(prev => (prev || []).map(m => m.modul_id === modul_id ? { ...m, ...data } : m));
    hentStruktur();
  };

  const sletModulConfirm = async (modul) => {
    await axios.delete(`${API}/undervisning/${retning}/moduler/${modul.modul_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setStruktur(prev => (prev || []).filter(m => m.modul_id !== modul.modul_id));
    hentStruktur();
  };

  // ── CRUD-handlers: emner ───────────────────────────────────────────────────
  const opretEmne = async (modul_id, data) => {
    const r = await axios.post(`${API}/undervisning/${retning}/moduler/${modul_id}/emner`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const ny = r.data?.emne || r.data;
    setStruktur(prev => (prev || []).map(m =>
      m.modul_id === modul_id
        ? { ...m, emner: [...(m.emner || []), ny] }
        : m,
    ));
    hentStruktur();
  };

  const redigerEmne = async (modul_id, emne_id, data) => {
    await axios.put(`${API}/undervisning/${retning}/emner/${emne_id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setStruktur(prev => (prev || []).map(m =>
      m.modul_id === modul_id
        ? { ...m, emner: (m.emner || []).map(e => e.emne_id === emne_id ? { ...e, ...data } : e) }
        : m,
    ));
    hentStruktur();
  };

  const sletEmneConfirm = async (modul_id, emne) => {
    await axios.delete(`${API}/undervisning/${retning}/emner/${emne.emne_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setStruktur(prev => (prev || []).map(m =>
      m.modul_id === modul_id
        ? { ...m, emner: (m.emner || []).filter(e => e.emne_id !== emne.emne_id) }
        : m,
    ));
    hentStruktur();
  };

  // ── Omsorter (DnD) ─────────────────────────────────────────────────────────
  const reorderArray = (arr, fromId, toId, idKey) => {
    const fromIdx = arr.findIndex(x => x[idKey] === fromId);
    const toIdx = arr.findIndex(x => x[idKey] === toId);
    if (fromIdx === -1 || toIdx === -1) return arr;
    const next = [...arr];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    return next;
  };

  const omsorterModuler = async (draggedId, targetId) => {
    if (draggedId === targetId) return;
    const next = reorderArray(struktur || [], draggedId, targetId, "modul_id");
    setStruktur(next);
    try {
      await axios.put(
        `${API}/undervisning/${retning}/moduler/sorter`,
        { modul_ids: next.map(m => m.modul_id) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      alert("Kunne ikke gemme rækkefølge");
      hentStruktur();
    }
  };

  const omsorterEmner = async (modul_id, draggedId, targetId) => {
    if (draggedId === targetId) return;
    let nyeEmner = [];
    setStruktur(prev => (prev || []).map(m => {
      if (m.modul_id !== modul_id) return m;
      nyeEmner = reorderArray(m.emner || [], draggedId, targetId, "emne_id");
      return { ...m, emner: nyeEmner };
    }));
    try {
      await axios.put(
        `${API}/undervisning/${retning}/moduler/${modul_id}/emner/sorter`,
        { emne_ids: nyeEmner.map(e => e.emne_id) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      alert("Kunne ikke gemme rækkefølge");
      hentStruktur();
    }
  };

  const omsorterFiler = async (emne_id, draggedId, targetId) => {
    if (draggedId === targetId) return;
    const filer = filerByEmne[emne_id] || [];
    const next = reorderArray(filer, draggedId, targetId, "fil_id");
    setFilerByEmne(prev => ({ ...prev, [emne_id]: next }));
    try {
      await axios.put(
        `${API}/undervisning/${retning}/emner/${emne_id}/filer/sorter`,
        { fil_ids: next.map(f => f.fil_id) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      alert("Kunne ikke gemme rækkefølge");
      // Genhent filerne
      axios.get(`${API}/undervisning/${retning}/emner/${emne_id}/filer`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => handleFilerLoaded(emne_id, r.data?.filer || r.data || []));
    }
  };

  const flytFil = async (fil_id, target_emne_id) => {
    let movedFil = null;
    setFilerByEmne(prev => {
      const next = {};
      for (const eid of Object.keys(prev)) {
        const remaining = (prev[eid] || []).filter(f => {
          if (f.fil_id === fil_id) { movedFil = f; return false; }
          return true;
        });
        next[eid] = remaining;
      }
      if (movedFil) {
        next[target_emne_id] = [...(next[target_emne_id] || []), movedFil];
      }
      return next;
    });
    try {
      await axios.put(
        `${API}/undervisning/filer/${fil_id}/flyt`,
        { target_emne_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      alert("Kunne ikke flytte fil");
      hentStruktur();
    }
  };

  // Drag handlers for moduler
  const handleModulDragStart = (e, modul_id) => {
    e.dataTransfer.setData("text/plain", `modul:${modul_id}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleModulDragOver = (e, modul_id) => {
    if (!(e.dataTransfer.types || []).includes("text/plain")) return;
    e.preventDefault();
    setModulDropTarget(modul_id);
  };
  const handleModulDrop = (e, target_modul_id) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    setModulDropTarget(null);
    if (!raw.startsWith("modul:")) return;
    const draggedId = raw.slice(6);
    omsorterModuler(draggedId, target_modul_id);
  };

  // Standardstruktur-import
  const importerStandard = async () => {
    setImporterer(true);
    try {
      await axios.post(`${API}/undervisning/${retning}/importer-standard`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      hentStruktur();
    } catch (e) {
      alert(e.response?.data?.detail || "Kunne ikke importere standardstruktur");
    }
    setImporterer(false);
  };

  const retningLabel = RETNING_LABEL[retning] || retning;
  const visTabBar = !forceRetning && !isElev && (isAdmin || isMember);

  if (!user) return null;

  const body = (
    <>
        {/* Top-række: retnings-undermenu til venstre, søg + log til højre.
            Samme SubTabs-skabelon som de øvrige sider. */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            {visTabBar ? (
              <SubTabs
                tabs={[
                  { key: "kosmetolog", label: RETNING_LABEL.kosmetolog },
                  { key: "frisor", label: RETNING_LABEL.frisor },
                ]}
                value={retning}
                onChange={setRetning}
              />
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={soegning}
                onChange={e => setSoegning(e.target.value)}
                placeholder="Søg..."
                className="bg-secondary border border-border rounded-lg pl-9 pr-3 h-9 text-sm w-48 sm:w-64 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
              {soegerNu && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>
        </div>

        {/* Indhold */}
        {!retningResolved || strukturLoading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#FF2A6D]" />
            <p className="text-[13px] text-muted-foreground">Henter undervisningsmateriale...</p>
          </div>
        ) : strukturError ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <p className="text-sm text-red-500">{strukturError}</p>
          </div>
        ) : soegeResultater ? (
          <SoegeResultater
            results={soegeResultater}
            token={token}
            retning={retning}
            isAdmin={isAdmin}
          />
        ) : !struktur || struktur.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            {canUpload ? (
              <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center w-full max-w-lg">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
                <p className="text-sm text-muted-foreground mb-4">Ingen moduler endnu</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                  <button
                    onClick={importerStandard}
                    disabled={importerer}
                    className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {importerer
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <>Importér standardstruktur for {RETNING_LABEL[retning]}</>}
                  </button>
                  <button
                    onClick={() => setModulModal({ mode: "opret" })}
                    className="px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" /> Nyt modul
                  </button>
                </div>
              </div>
            ) : (
              <>
                <FileText className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Ingen moduler endnu</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {struktur.map((modul, i) => (
              <ModulCard
                key={modul.modul_id}
                modul={modul}
                index={i}
                retning={retning}
                token={token}
                isOpen={aabneModuler.has(modul.modul_id)}
                onToggle={() => toggleModul(modul.modul_id)}
                aabneEmner={aabneEmner}
                onToggleEmne={toggleEmne}
                filerByEmne={filerByEmne}
                onFilerLoaded={handleFilerLoaded}
                canUpload={canUpload}
                isAdmin={isAdmin}
                onEmneBreadcrumb={handleEmneBreadcrumb}
                onRedigerModul={(m) => setModulModal({ mode: "rediger", modul: m })}
                onSletModul={(m) => setSletModul(m)}
                onRedigerEmne={(e) => setEmneModal({ mode: "rediger", modul, emne: e })}
                onSletEmne={(e) => setSletEmne({ modul, emne: e })}
                onNytEmne={(m) => setEmneModal({ mode: "opret", modul: m })}
                onDragStart={handleModulDragStart}
                onDragOver={handleModulDragOver}
                onDrop={handleModulDrop}
                dropTarget={modulDropTarget}
                onOmsorterEmner={omsorterEmner}
                onOmsorterFiler={omsorterFiler}
                onFlytFil={flytFil}
              />
            ))}
            {/* "Nyt modul"-knap flyttet ned UNDER kasserne. */}
            {canUpload && (
              <div className="flex justify-end pt-1">
                <button
                  onClick={() => setModulModal({ mode: "opret" })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-3.5 h-3.5" /> Nyt modul
                </button>
              </div>
            )}
          </div>
        )}

      {visLog && isSuperAdmin && (
        <AktivitetslogModal token={token} onClose={() => setVisLog(false)} />
      )}

      {modulModal && (
        <ModulModal
          titel={modulModal.mode === "opret" ? "Opret modul" : "Rediger modul"}
          initial={modulModal.modul || {}}
          foreslaaetRaekkefolge={(struktur || []).length}
          onClose={() => { setModulDirty(false); setModulModal(null); }}
          onDirtyChange={setModulDirty}
          onSaved={showToast}
          onSave={(data) =>
            modulModal.mode === "opret"
              ? opretModul(data)
              : redigerModul(modulModal.modul.modul_id, data)
          }
        />
      )}

      {emneModal && (
        <EmneModal
          titel={emneModal.mode === "opret" ? "Opret emne" : "Rediger emne"}
          initial={emneModal.emne || {}}
          foreslaaetRaekkefolge={(emneModal.modul?.emner || []).length}
          onClose={() => { setEmneDirty(false); setEmneModal(null); }}
          onDirtyChange={setEmneDirty}
          onSaved={showToast}
          onSave={(data) =>
            emneModal.mode === "opret"
              ? opretEmne(emneModal.modul.modul_id, data)
              : redigerEmne(emneModal.modul.modul_id, emneModal.emne.emne_id, data)
          }
        />
      )}

      {sletModul && (
        <ConfirmModal
          titel="Slet modul"
          besked={`Vil du slette modulet "${modulTitel(sletModul)}"? Alle tilhørende emner og filer slettes også.`}
          onClose={() => setSletModul(null)}
          onConfirm={() => sletModulConfirm(sletModul)}
        />
      )}

      {sletEmne && (
        <ConfirmModal
          titel="Slet emne"
          besked={`Vil du slette emnet "${emneTitel(sletEmne.emne)}"? Alle tilhørende filer slettes også.`}
          onClose={() => setSletEmne(null)}
          onConfirm={() => sletEmneConfirm(sletEmne.modul.modul_id, sletEmne.emne)}
        />
      )}

      {/* Bekræftelses-toast (fixed bottom). En simpel let komponent uden ekstra deps. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium shadow-lg animate-in fade-in slide-in-from-bottom-2"
        >
          {toast}
        </div>
      )}
    </>
  );

  if (embedded) return <div className="pb-4">{body}</div>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={logout} />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-12">
        {body}
      </main>
    </div>
  );
}

// Standalone-route wrapper (bruges via /undervisning)
export default function UndervisningPage() {
  return <UndervisningView embedded={false} />;
}
